import * as React from 'react'
import { Check, ChevronRight } from 'lucide-react'
import type { TreeProblemDto } from '@lib/dto/tree-problem.dto'
import type { TreeProblemTypeDto } from '@lib/dto/tree-problem-type.dto'
import { JOB_TYPE_LABELS, jobTypeHasLevel } from '@lib/enums/job-type.enum'
import { PROBLEM_SEVERITY_LABELS } from '@lib/enums/problem-severity.enum'
import { formatMinutesShort } from '@lib/helpers/minutes'
import { Button } from '@/components/ui/button'
import { formatVisitDay } from '@/site-visits/VisitBar'
import {
  bringTreeProblemForward,
  treeProblemState,
  type LastVisitTree,
  type StillHereState,
} from '@/site-visits/lastVisit'
import { cn } from '@/lib/utils'

/**
 * What the tree sheet shows of the visit before this one.
 *
 * Loading, missing, and ready are three states. Two booleans let a caller
 * say the findings are both still loading and already gone, and then the
 * sheet has to pick one.
 */
export type TreeLastVisitProps = {
  /** The day the earlier visit started. */
  startedOn: string
} & (
  | { status: 'loading' }
  | { status: 'missing' }
  | {
      status: 'ready'
      /** What it found on this tree; null for nothing recorded. */
      found: LastVisitTree | null
    }
)

function plural(count: number, noun: string): string {
  return `${String(count)} ${noun}${count === 1 ? '' : 's'}`
}

/** "2 problems · crown cleaning", what the closed section says it holds. */
function summary(found: LastVisitTree | null): string {
  if (found === null) return 'Nothing recorded'
  const parts = [
    found.problems.length === 0
      ? null
      : plural(found.problems.length, 'problem'),
    ...found.jobs.map((job) => JOB_TYPE_LABELS[job.type].toLowerCase()),
    found.noPruningRecommended ? 'no pruning' : null,
    found.removalOffered ? 'removal' : null,
  ].filter((part): part is string => part !== null)
  return parts.length === 0 ? 'Nothing recorded' : parts.join(' · ')
}

/** The work last visit recommended, a line each. */
function workLines(found: LastVisitTree): string[] {
  const jobs = found.jobs.map((job) => {
    const level = jobTypeHasLevel(job.type) ? ` L${String(job.level)}` : ''
    const minutes = formatMinutesShort(job.minutes)
    const note = job.notes === null ? '' : ` (${job.notes})`
    return `${JOB_TYPE_LABELS[job.type]}${level}${note}${minutes === null ? '' : ` · ${minutes}`}`
  })
  const estimate = formatMinutesShort(found.estimatedLaborMinutes)
  const removal = formatMinutesShort(found.removalMinutes)
  return [
    ...jobs,
    found.noPruningRecommended ? 'No pruning recommended' : null,
    estimate === null || found.noPruningRecommended
      ? null
      : `Estimated labor ${estimate}`,
    found.removalOffered
      ? `Removal quoted${removal === null ? '' : ` · ${removal}`}${found.removalNotes === null ? '' : `: ${found.removalNotes}`}`
      : null,
    found.isOptional ? 'Offered as optional work' : null,
  ].filter((line): line is string => line !== null)
}

function problemName(
  problem: TreeProblemDto,
  typesById: ReadonlyMap<string, TreeProblemTypeDto>,
): string {
  if (problem.typeId === null) return 'Other'
  return typesById.get(problem.typeId)?.name ?? 'Unknown problem'
}

/** The Still here control for one problem, or what stands in for it. */
function StillHere({
  state,
  controlClassName,
  onBring,
}: {
  state: StillHereState
  controlClassName?: string | undefined
  onBring: () => void
}) {
  if (state === 'brought') {
    return (
      <span className="flex shrink-0 items-center gap-1 text-xs text-secondary-text">
        <Check className="h-3.5 w-3.5 text-success" />
        Still here
      </span>
    )
  }
  if (state === 'retired') {
    return (
      <span className="shrink-0 text-xs text-muted-text">
        No longer in the list
      </span>
    )
  }
  if (state === 'unchecked') {
    return (
      <span className="shrink-0 text-xs text-muted-text">Needs a signal</span>
    )
  }
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={cn('shrink-0', controlClassName)}
      onClick={onBring}
    >
      Still here
    </Button>
  )
}

/**
 * "Last visit" on a revisit's tree sheet: what the visit before this one
 * found and recommended, read-only, with Still here on each problem to bring
 * it into this visit (docs/site-visits.md). Closed to start with, so a sheet
 * opened to change a species is not a screen of last year's notes.
 */
export function TreeLastVisit({
  lastVisit,
  problems,
  problemTypes,
  controlClassName,
  onChange,
}: {
  lastVisit: TreeLastVisitProps
  /** This visit's problems on the tree. */
  problems: TreeProblemDto[]
  /** The whole directory, archived entries included. */
  problemTypes: TreeProblemTypeDto[]
  controlClassName?: string | undefined
  onChange: (problems: TreeProblemDto[]) => void
}) {
  const [open, setOpen] = React.useState(false)
  const typesById = React.useMemo(
    () => new Map(problemTypes.map((type) => [type.id, type])),
    [problemTypes],
  )
  function renderBody(): React.ReactNode {
    if (lastVisit.status === 'loading') {
      return <p className="text-xs text-muted-text">Loading last visit…</p>
    }
    if (lastVisit.status === 'missing') {
      return (
        <p className="text-xs text-muted-text">
          Last visit is not on this phone. Open the site with a signal to bring
          it.
        </p>
      )
    }
    const { found } = lastVisit
    if (found === null) {
      return (
        <p className="text-xs text-muted-text">
          Nothing was recorded on this tree.
        </p>
      )
    }
    const work = workLines(found)
    return (
      <div className="space-y-2">
        {found.problems.length === 0 ? null : (
          <ul className="divide-y divide-default rounded-md border border-default bg-surface">
            {found.problems.map((problem) => (
              <li
                key={problem.id}
                className="flex items-center justify-between gap-2 px-2.5 py-2"
              >
                <div className="min-w-0 space-y-0.5">
                  <p className="truncate text-sm font-medium">
                    {problemName(problem, typesById)}
                  </p>
                  <p className="text-xs text-secondary-text">
                    {problem.severity === null
                      ? 'Not rated'
                      : PROBLEM_SEVERITY_LABELS[problem.severity]}
                    {problem.notes === null ? '' : ` · ${problem.notes}`}
                  </p>
                  {problem.solutions.length === 0 ? null : (
                    <p className="text-xs text-muted-text">
                      {problem.solutions
                        .map((solution) => solution.label)
                        .join(', ')}
                    </p>
                  )}
                </div>
                <StillHere
                  state={treeProblemState(problem, problems, typesById)}
                  controlClassName={controlClassName}
                  onBring={() => {
                    onChange(
                      bringTreeProblemForward(problems, problem, typesById),
                    )
                  }}
                />
              </li>
            ))}
          </ul>
        )}
        {work.length === 0 ? null : (
          <div className="space-y-0.5">
            <p className="text-xs font-medium text-secondary-text">
              Recommended then
            </p>
            {work.map((line) => (
              <p key={line} className="text-xs text-secondary-text">
                {line}
              </p>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <section className="rounded-md border border-default bg-subtle">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          setOpen((current) => !current)
        }}
        className={cn(
          'flex w-full items-center gap-1.5 px-2.5 text-left text-sm',
          controlClassName,
        )}
      >
        <ChevronRight
          className={cn(
            'h-3.5 w-3.5 shrink-0 text-muted-text transition-transform',
            open ? 'rotate-90' : null,
          )}
        />
        <span className="font-medium">
          Last visit · {formatVisitDay(lastVisit.startedOn)}
        </span>
        <span className="ml-auto truncate text-xs text-muted-text">
          {lastVisit.status === 'ready' ? summary(lastVisit.found) : ''}
        </span>
      </button>
      {open ? <div className="px-2.5 pb-2.5">{renderBody()}</div> : null}
    </section>
  )
}
